import { useCallback, useMemo, useRef, useState, type ReactNode } from "react"

import { XMarkIcon } from "@heroicons/react/20/solid"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom"
import { RecoilRoot, useRecoilCallback, useRecoilValue } from "recoil"

import { invalidationIntents, USER_KEYS } from "~/api/cache"
import type {
  SolarDesignerAvailableDto,
  SolarDesignSaveResultDto,
  SolarDesignerSummaryDto,
} from "~/api/dto/solar-proposal-designer.dto"
import type { UserProfileDto } from "~/api/dto/user.dto"
import useSolarDesignVersionCommand from "~/api/hooks/useSolarDesignVersionCommand"
import useSolarDesignVersions from "~/api/hooks/useSolarDesignVersions"
import { getSolarProposalErrorStatus } from "~/api/hooks/useSolarProposal"
import useSolarProposalDesigner from "~/api/hooks/useSolarProposalDesigner"
import { userService } from "~/api/services/user.service"
import {
  SOLAR_DESIGN_SUPERSEDED_CODE,
  solarProposalErrorCode,
  solarProposalErrorMessages,
} from "~/api/solar-proposal-errors"
import { ErrorBoundary } from "~/components/ErrorBoundary"
import {
  DesignerCapabilitiesProvider,
  DesignerShell,
  SHELL_ONLY_DESIGNER_FEATURES,
  type ContinuePhase,
  type DesignerCapabilities,
} from "~/containers/LegacyDesigner"
import {
  phaseState,
  useEnterNextPhase,
} from "~/containers/LegacyDesigner/state/phase"
import { DesignActionsProvider } from "~/containers/Proposal/state/designs/design-context"
import {
  designDiffState,
  designRemoteState,
} from "~/containers/Proposal/state/designs/design-state"
import { enterNextPhaseAndSave } from "~/containers/Proposal/state/designs/use-design"
import { ReadOnlyProposalContextProvider } from "~/containers/Proposal/state/proposal/proposal-context"
import type { User } from "~/graphql/client"
import { useExitPrompt } from "~/hooks/useExitPrompt"

import { readDesignerNotices } from "./designer-notices-state"
import { hydrateDesignerState } from "./hydrate-designer-state"
import { SolarDesignVersionChip } from "./SolarDesignVersionChip"
import { DUPLICATE_PRODUCTION_WARNING } from "../SolarDesignVersions"
import { useRestDesignActions } from "./useRestDesignActions"
import { PINNED_BATTERY_PROPOSAL_THEME } from "../../BatteryOnlyProposal/theme/battery-proposal-theme"
import {
  SOLAR_PROPOSAL_ERROR_MESSAGES,
  SolarLoadState,
} from "../solar-proposal-load-state"
import { solarProposalPath, solarSetupStepPath } from "../solar-setup-paths"

export const DESIGN_SUPERSEDED_MESSAGE = `This design changed elsewhere. Reload to continue.`
export const DUPLICATE_UNSAVED_MESSAGE = `Your changes couldn't be saved, so this version wasn't duplicated.`
export const SWITCH_UNSAVED_MESSAGE = `You have unsaved changes. Leaving discards them.`

const SAVE_WARNING_MESSAGES = {
  equipment_removed_incompatible: `Equipment that no longer fits the inverter was removed.`,
  production_not_updated: `Saved, but production couldn't be updated. Retry it from System Design.`,
} as const

type DesignerNotice = {
  id: number
  tone: `danger` | `info` | `warning`
  text: string
  /** A notice that needs the seller (a stale design) stays until they act. */
  action?: { label: string; run: () => void }
  /** An action the seller may also decline (dismiss) rather than must take. */
  dismissible?: boolean
}

const DesignerState = ({ children }: { children: ReactNode }): JSX.Element => (
  <div
    data-battery-theme={PINNED_BATTERY_PROPOSAL_THEME}
    className="flex min-h-screen flex-col items-center justify-center gap-4 bg-bp-page px-6 text-center text-bp-text"
  >
    {children}
  </div>
)

const NOTICE_TONES: Record<DesignerNotice[`tone`], string> = {
  info: `border-border bg-surface`,
  warning: `border-warning/40 bg-surface`,
  danger: `border-danger/40 bg-surface`,
}

/** The designer's save outcomes: warnings, failures and the stale-design reload. */
const DesignerNotices = ({
  notices,
  onDismiss,
}: {
  notices: readonly DesignerNotice[]
  onDismiss: (id: number) => void
}): JSX.Element | null =>
  notices.length === 0 ? null : (
    <div
      data-testid="solar-designer-notices"
      className="fixed inset-x-4 top-20 z-[2147483001] mx-auto flex max-w-md flex-col gap-2"
    >
      {notices.map((notice) => (
        <div
          key={notice.id}
          role={notice.tone === `info` ? `status` : `alert`}
          className={`flex items-start gap-3 rounded-xl border p-4 text-sm text-foreground shadow-lg ${NOTICE_TONES[notice.tone]}`}
        >
          <p className="flex-1 font-semibold">{notice.text}</p>
          {notice.action ? (
            <button
              type="button"
              className="font-semibold text-primary underline"
              onClick={notice.action.run}
            >
              {notice.action.label}
            </button>
          ) : null}
          {notice.action && !notice.dismissible ? null : (
            <button
              type="button"
              aria-label="Dismiss"
              className="text-muted hover:text-foreground"
              onClick={() => onDismiss(notice.id)}
            >
              <XMarkIcon aria-hidden="true" className="size-5" />
            </button>
          )}
        </div>
      ))}
    </div>
  )

/** Inside the scoped RecoilRoot: REST actions, capabilities and the shell. */
const SolarDesignerHost = ({
  proposalId,
  bundle,
  onBack,
  onReload,
  initialNotices,
}: {
  proposalId: string
  bundle: SolarDesignerAvailableDto
  onBack: () => void
  /** Re-reads the bundle and remounts; `carry` notices show after the remount. */
  onReload: (carry?: string[]) => void
  initialNotices: readonly string[]
}): JSX.Element => {
  const [summary, setSummary] = useState<SolarDesignerSummaryDto>(bundle.summary)
  const [notices, setNotices] = useState<DesignerNotice[]>(() =>
    initialNotices.map((text, index) => ({
      id: index + 1,
      tone: `warning`,
      text,
    })),
  )
  const nextNoticeId = useRef(initialNotices.length + 1)

  const pushNotices = useCallback((added: Array<Omit<DesignerNotice, `id`>>) => {
    const withIds = added.map((notice) => ({
      ...notice,
      id: nextNoticeId.current++,
    }))
    setNotices((current) => [...current, ...withIds])
  }, [])

  const queryClient = useQueryClient()
  const onSaved = useCallback(
    (result: SolarDesignSaveResultDto) => {
      setSummary(result.summary)
      // The proposal's reads show the save whenever the seller leaves.
      void invalidationIntents.solarDesignSaved(queryClient, proposalId)
      pushNotices(
        result.warnings.map((warning) => ({
          tone: warning === `production_not_updated` ? `warning` : `info`,
          text:
            warning === `production_not_updated` &&
            result.productionNotUpdatedReason
              ? result.productionNotUpdatedReason
              : SAVE_WARNING_MESSAGES[warning],
        })),
      )
    },
    [proposalId, pushNotices, queryClient],
  )

  const pushSuperseded = useCallback(
    () =>
      pushNotices([
        {
          tone: `danger`,
          text: DESIGN_SUPERSEDED_MESSAGE,
          action: { label: `Reload`, run: () => onReload() },
        },
      ]),
    [onReload, pushNotices],
  )

  const { actions, status, whenIdle } = useRestDesignActions({
    proposalId,
    initialVersion: bundle.updatedAt,
    canResetDesign: bundle.viewerCanResetDesign,
    events: {
      onSaved,
      onSuperseded: pushSuperseded,
      onFailed: (messages) =>
        pushNotices(messages.map((text) => ({ tone: `danger`, text }))),
    },
  })

  // The version chip: duplicate the design being edited, or go pick another.
  const versions = useSolarDesignVersions(proposalId)
  // `mutate` is stable; the mutation result object is new on every render.
  const { mutate: mutateVersion, isLoading: duplicating } =
    useSolarDesignVersionCommand()
  const designId = useRecoilValue(designRemoteState).id
  const hasUnsavedEdits = useRecoilCallback(
    ({ snapshot }) =>
      () =>
        snapshot.getLoadable(designDiffState).getValue() !== null,
    [],
  )
  /** The save in flight, then any edit not sent yet; true when nothing is left unsaved. */
  const flushEdits = useCallback(async (): Promise<boolean> => {
    await whenIdle()
    await actions.save()
    await whenIdle()
    return !hasUnsavedEdits()
  }, [actions, hasUnsavedEdits, whenIdle])

  const duplicateVersion = useCallback(async () => {
    if (!(await flushEdits())) {
      pushNotices([{ tone: `danger`, text: DUPLICATE_UNSAVED_MESSAGE }])
      return
    }
    mutateVersion(
      { kind: `duplicate`, proposalId, expectedActiveDesignId: designId },
      {
        // The copy is the active design now: remount the designer on it.
        onSuccess: (saved) =>
          onReload(
            saved.warnings.includes(`production_not_updated`)
              ? [DUPLICATE_PRODUCTION_WARNING]
              : [],
          ),
        onError: (error) => {
          if (solarProposalErrorCode(error) === SOLAR_DESIGN_SUPERSEDED_CODE) {
            pushSuperseded()
            return
          }
          const messages = solarProposalErrorMessages(error)
          pushNotices(
            (messages.length > 0
              ? messages
              : [`This version couldn't be duplicated.`]
            ).map((text) => ({ tone: `danger`, text })),
          )
        },
      },
    )
  }, [
    designId,
    flushEdits,
    onReload,
    proposalId,
    pushNotices,
    pushSuperseded,
    mutateVersion,
  ])

  // As Back does: unsaved edits prompt first and are discarded on leaving.
  const switchVersion = useCallback(() => {
    const leave = (): void => {
      if (hasUnsavedEdits()) actions.resetLocal()
      void whenIdle().then(onBack)
    }
    if (!hasUnsavedEdits()) {
      leave()
      return
    }
    // One prompt at a time; the seller may dismiss it and keep editing.
    if (notices.some(({ text }) => text === SWITCH_UNSAVED_MESSAGE)) return
    pushNotices([
      {
        tone: `warning`,
        text: SWITCH_UNSAVED_MESSAGE,
        action: { label: `Discard and leave`, run: leave },
        dismissible: true,
      },
    ])
  }, [actions, hasUnsavedEdits, notices, onBack, pushNotices, whenIdle])

  // Memoized: the chip rides in the capabilities, whose identity re-renders the shell.
  const versionChip = useMemo(
    () => (
      <SolarDesignVersionChip
        versions={versions.data}
        designId={designId}
        busy={duplicating}
        onDuplicate={() => void duplicateVersion()}
        onSwitch={switchVersion}
      />
    ),
    [designId, duplicateVersion, switchVersion, duplicating, versions.data],
  )

  const delta = useRecoilValue(designDiffState)
  useExitPrompt(delta !== null || status.isSaving)

  const enterNextPhase = useEnterNextPhase()
  const phase = useRecoilValue(phaseState)
  const continuePhase = useCallback<ContinuePhase>(
    () =>
      enterNextPhaseAndSave({
        enterNextPhase,
        save: actions.save,
        changeLocal: actions.changeLocal,
        markCompleted: phase === `Panel Placement`,
      }),
    [actions.changeLocal, actions.save, enterNextPhase, phase],
  )

  const capabilities = useMemo(
    (): DesignerCapabilities => ({
      ...SHELL_ONLY_DESIGNER_FEATURES,
      backTarget: {
        kind: `custom`,
        label: `Back to proposal`,
        // A save in flight finishes first, so the step reads what it wrote.
        navigate: () => void whenIdle().then(onBack),
      },
      continuePhase,
      systemInfo: {
        kind: `provided`,
        annualProductionKwh: summary.annualProductionKwh,
        energyOffset: summary.offsetPct == null ? null : summary.offsetPct / 100,
        isRecalculating: status.isSaving,
      },
      topBarAccessory: versionChip,
    }),
    [continuePhase, onBack, status.isSaving, summary, versionChip, whenIdle],
  )

  return (
    <DesignActionsProvider actions={actions} status={status}>
      <DesignerCapabilitiesProvider capabilities={capabilities}>
        <DesignerShell />
        {duplicating ? (
          // The copy is taken from what was saved: no edit lands while it runs.
          <div
            role="status"
            data-testid="solar-designer-duplicating"
            className="fixed inset-0 z-[2147483000] flex items-center justify-center bg-background/60 text-sm font-semibold text-foreground"
          >
            Duplicating this version…
          </div>
        ) : null}
        <DesignerNotices
          notices={notices}
          onDismiss={(id) =>
            setNotices((current) => current.filter((notice) => notice.id !== id))
          }
        />
      </DesignerCapabilitiesProvider>
    </DesignActionsProvider>
  )
}

/**
 * The desktop designer on Solar Proposal V2, full screen and outside the
 * proposal shell. It reuses V1's Recoil design state through a scoped
 * RecoilRoot, hydrated from `GET …/designer` and the REST current user, and
 * saves over `PATCH …/design`, with no GraphQL network client. Back returns to
 * the System Design step and refreshes the reads a save leaves stale.
 */
export const SolarDesignerRoute = (): JSX.Element => {
  const { proposalId = `` } = useParams<{ proposalId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const designer = useSolarProposalDesigner(proposalId)
  const profile = useQuery({
    queryKey: USER_KEYS.profile(),
    queryFn: (): Promise<UserProfileDto> => userService.getProfile(),
    retry: false,
  })
  // A reload re-reads the bundle, then remounts the RecoilRoot to re-hydrate.
  const [generation, setGeneration] = useState(0)
  const [reloading, setReloading] = useState(false)

  const onBack = useCallback(() => {
    navigate(solarSetupStepPath(proposalId, `design`))
    void invalidationIntents.solarDesignSaved(queryClient, proposalId)
  }, [navigate, proposalId, queryClient])

  // Notices for the next mount: from the step that opened the designer, or a reload.
  const location = useLocation()
  const [carriedNotices, setCarriedNotices] = useState<string[]>(() =>
    readDesignerNotices(location.state),
  )

  const onReload = useCallback(
    (carry: string[] = []) => {
      setCarriedNotices(carry)
      setReloading(true)
      void designer
        .refetch()
        .then(() => setGeneration((value) => value + 1))
        .finally(() => setReloading(false))
    },
    [designer],
  )

  if (designer.isError || profile.isError) {
    const status = designer.isError
      ? getSolarProposalErrorStatus(designer.error)
      : getSolarProposalErrorStatus(profile.error)
    return (
      <SolarLoadState>
        {SOLAR_PROPOSAL_ERROR_MESSAGES[status ?? 0] ??
          `Unable to load the designer`}
      </SolarLoadState>
    )
  }

  // Hydrate only from a read this visit fetched: the cache may hold a design
  // another step has since changed.
  if (
    !designer.data ||
    !designer.isFetchedAfterMount ||
    !profile.data ||
    reloading
  ) {
    return <SolarLoadState>Loading the designer</SolarLoadState>
  }

  const bundle = designer.data
  if (bundle.status === `unavailable`) {
    return (
      <DesignerState>
        <p role="status" data-testid="solar-designer-unavailable">
          {bundle.message}
        </p>
        <button
          type="button"
          className="font-semibold text-bp-accent underline"
          onClick={() => navigate(solarSetupStepPath(proposalId, `design`))}
        >
          Back to System Design
        </button>
      </DesignerState>
    )
  }

  // Editing needs Proposal Update; a viewer without it never meets the designer.
  if (!bundle.viewerCanUpdate) {
    return <Navigate to={solarProposalPath(proposalId)} replace />
  }

  return (
    <RecoilRoot
      key={generation}
      initializeState={hydrateDesignerState(
        bundle,
        profile.data as unknown as Partial<User>,
      )}
    >
      <ErrorBoundary>
        <ReadOnlyProposalContextProvider>
          <SolarDesignerHost
            proposalId={proposalId}
            bundle={bundle}
            onBack={onBack}
            onReload={onReload}
            initialNotices={carriedNotices}
          />
        </ReadOnlyProposalContextProvider>
      </ErrorBoundary>
    </RecoilRoot>
  )
}
